/**
 * Shared normalization for the host → iframe `calendly.consent.update.v1` payload.
 *
 * Used by both:
 *   - `consent_update_handoff.js` (transport: postMessage → in-process subscribers)
 *   - `consent_module.js`               (state: applies host signal to ConsentState)
 *
 * Keeping a single source of truth avoids drift between transport and state layers.
 */

const HOST_CONSENT_VALUES = Object.freeze(['granted', 'denied', 'unknown'])
const HOST_CONSENT_VALUE_SET = new Set(HOST_CONSENT_VALUES)

export const CONSENT_CATEGORIES = Object.freeze([
  'necessary',
  'functional',
  'performance',
  'targeting',
])

const KNOWN_FRAMEWORKS = new Set(['custom', 'onetrust', 'iab_tcf_v2', 'gpp'])

const normalizeCategories = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const categories = {}
  CONSENT_CATEGORIES.forEach((key) => {
    const value = raw[key]
    if (HOST_CONSENT_VALUE_SET.has(value)) categories[key] = value
  })
  return categories
}

/**
 * Normalize a raw v1 host consent payload (the object under `data.consent` from the
 * postMessage). Returns null when the payload is unusable (missing source, no recognized
 * categories, etc.) so callers can drop it.
 *
 * `source` is treated as an open enum: any non-empty string is preserved so that future
 * host source values (e.g. "host-manual") don't get silently dropped.
 *
 * @param {unknown} raw
 * @returns {null | {
 *   source: string,
 *   categories: Partial<Record<'necessary'|'functional'|'performance'|'targeting','granted'|'denied'|'unknown'>>,
 *   framework?: 'custom'|'onetrust'|'iab_tcf_v2'|'gpp',
 *   updatedAt?: string,
 * }}
 */
export const normalizeHostConsent = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  if (typeof raw.source !== 'string' || !raw.source) return null

  const categories = normalizeCategories(raw.categories)
  if (!categories || Object.keys(categories).length === 0) return null

  const normalized = {
    source: raw.source,
    categories,
  }
  if (raw.framework && KNOWN_FRAMEWORKS.has(raw.framework)) {
    normalized.framework = raw.framework
  }
  if (typeof raw.updatedAt === 'string' && raw.updatedAt) {
    normalized.updatedAt = raw.updatedAt
  }
  return normalized
}
